import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, Bell, Video, MessageSquare, BookOpen, CreditCard, Users, Settings } from 'lucide-react';
import { notificationsApi } from '../../lib/api';

// Toast Notification Types
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number; // ms, 0 for persistent
  action?: {
    label: string;
    onClick: () => void;
  };
  timestamp: Date;
  read?: boolean;
}

// Backend notification from API
export interface BackendNotification {
  id: string;
  type: string;
  category: string;
  title: string;
  body: string;
  data: any;
  action_url: string | null;
  action_text: string | null;
  icon: string;
  read: boolean;
  read_at: string | null;
  created_at: string;
  time_ago: string;
}

export interface NotificationCenterItem {
  id: string;
  type: NotificationType;
  backendType?: string;
  title: string;
  message?: string;
  timestamp: Date;
  read: boolean;
  link?: string;
  icon?: string;
}

interface NotificationContextValue {
  // Toast notifications
  notifications: Notification[];
  showNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;

  // Notification center
  notificationCenterItems: NotificationCenterItem[];
  unreadCount: number;
  isLoading: boolean;
  addToNotificationCenter: (item: Omit<NotificationCenterItem, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeFromNotificationCenter: (id: string) => void;
  clearNotificationCenter: () => void;
  refreshNotifications: () => Promise<void>;

  // Notification center visibility
  isNotificationCenterOpen: boolean;
  openNotificationCenter: () => void;
  closeNotificationCenter: () => void;
  toggleNotificationCenter: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

// Toast component
function Toast({ notification, onRemove }: { notification: Notification; onRemove: () => void }) {
  const icons = {
    success: <CheckCircle size={20} className="text-green-500" />,
    error: <AlertCircle size={20} className="text-red-500" />,
    warning: <AlertTriangle size={20} className="text-yellow-500" />,
    info: <Info size={20} className="text-blue-500" />,
  };

  const backgrounds = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    warning: 'bg-yellow-50 border-yellow-200',
    info: 'bg-blue-50 border-blue-200',
  };

  useEffect(() => {
    if (notification.duration !== 0) {
      const timer = setTimeout(onRemove, notification.duration || 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.duration, onRemove]);

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg ${backgrounds[notification.type]} animate-slide-in`}
      role="alert"
    >
      <div className="flex-shrink-0">{icons[notification.type]}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900">{notification.title}</p>
        {notification.message && (
          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
        )}
        {notification.action && (
          <button
            onClick={notification.action.onClick}
            className="text-sm font-medium text-teal-600 hover:text-teal-700 mt-2"
          >
            {notification.action.label}
          </button>
        )}
      </div>
      <button
        onClick={onRemove}
        className="flex-shrink-0 p-1 hover:bg-black/5 rounded"
      >
        <X size={16} className="text-gray-400" />
      </button>
    </div>
  );
}

// Get icon component based on notification type
function getNotificationIcon(iconName: string) {
  const iconMap: Record<string, React.ReactNode> = {
    video: <Video size={16} className="text-purple-500" />,
    'message-square': <MessageSquare size={16} className="text-blue-500" />,
    'book-open': <BookOpen size={16} className="text-amber-500" />,
    book: <BookOpen size={16} className="text-teal-500" />,
    'user-plus': <Users size={16} className="text-green-500" />,
    mail: <MessageSquare size={16} className="text-pink-500" />,
    'credit-card': <CreditCard size={16} className="text-emerald-500" />,
    bell: <Bell size={16} className="text-gray-500" />,
  };
  return iconMap[iconName] || <Bell size={16} className="text-gray-500" />;
}

// Notification Center Panel
function NotificationCenterPanel({
  items,
  isLoading,
  onClose,
  onMarkAsRead,
  onMarkAllAsRead,
  onRemove,
  onClear,
  onRefresh,
}: {
  items: NotificationCenterItem[];
  isLoading: boolean;
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  onRefresh: () => void;
}) {
  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-4 top-16 w-96 max-h-[calc(100vh-100px)] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Notifications</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              className="text-xs text-gray-500 hover:text-gray-700"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
            {items.some((item) => !item.read) && (
              <button
                onClick={onMarkAllAsRead}
                className="text-xs text-teal-600 hover:text-teal-700"
              >
                Mark all as read
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && items.length === 0 ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-500">Loading notifications...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <Bell size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer group ${
                    !item.read ? 'bg-teal-50/50' : ''
                  }`}
                  onClick={() => {
                    onMarkAsRead(item.id);
                    if (item.link) {
                      window.location.href = item.link;
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      {item.icon ? getNotificationIcon(item.icon) : <Bell size={16} className="text-gray-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm ${!item.read ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                          {item.title}
                        </p>
                        {!item.read && (
                          <span className="w-2 h-2 rounded-full bg-teal-600" />
                        )}
                      </div>
                      {item.message && (
                        <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{item.message}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">{formatTime(item.timestamp)}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(item.id);
                      }}
                      className="flex-shrink-0 p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} className="text-gray-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={onClear}
              className="w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Clear all notifications
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// Provider component
export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationCenterItems, setNotificationCenterItems] = useState<NotificationCenterItem[]>([]);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCountFromApi, setUnreadCountFromApi] = useState(0);

  // Fetch notifications from API
  const refreshNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await notificationsApi.list({ per_page: 50 });
      const items: NotificationCenterItem[] = response.notifications.map((n: BackendNotification) => ({
        id: n.id,
        type: 'info' as NotificationType, // Map backend type to UI type
        backendType: n.type,
        title: n.title,
        message: n.body,
        timestamp: new Date(n.created_at),
        read: n.read,
        link: n.action_url || undefined,
        icon: n.icon,
      }));
      setNotificationCenterItems(items);
      setUnreadCountFromApi(response.meta.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch unread count periodically
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsApi.getUnreadCount();
        setUnreadCountFromApi(response.unread_count);
      } catch (error) {
        // Silently fail for unread count polling
      }
    };

    // Initial fetch
    fetchUnreadCount();

    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch full list when notification center opens
  useEffect(() => {
    if (isNotificationCenterOpen) {
      refreshNotifications();
    }
  }, [isNotificationCenterOpen, refreshNotifications]);

  // Toast notifications
  const showNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setNotifications((prev) => [...prev, { ...notification, id, timestamp: new Date() }]);
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Notification center
  const addToNotificationCenter = useCallback(
    (item: Omit<NotificationCenterItem, 'id' | 'timestamp' | 'read'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      setNotificationCenterItems((prev) => [
        { ...item, id, timestamp: new Date(), read: false },
        ...prev,
      ].slice(0, 50)); // Keep max 50 items
    },
    []
  );

  const markAsRead = useCallback(async (id: string) => {
    // Optimistic update
    setNotificationCenterItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, read: true } : item))
    );
    setUnreadCountFromApi((prev) => Math.max(0, prev - 1));

    // API call
    try {
      await notificationsApi.markAsRead(id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert on error
      setNotificationCenterItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, read: false } : item))
      );
      setUnreadCountFromApi((prev) => prev + 1);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setNotificationCenterItems((prev) => prev.map((item) => ({ ...item, read: true })));
    setUnreadCountFromApi(0);

    // API call
    try {
      await notificationsApi.markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      // Refresh on error
      refreshNotifications();
    }
  }, [refreshNotifications]);

  const removeFromNotificationCenter = useCallback(async (id: string) => {
    // Optimistic update
    const item = notificationCenterItems.find((n) => n.id === id);
    setNotificationCenterItems((prev) => prev.filter((item) => item.id !== id));
    if (item && !item.read) {
      setUnreadCountFromApi((prev) => Math.max(0, prev - 1));
    }

    // API call
    try {
      await notificationsApi.delete(id);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      // Refresh on error
      refreshNotifications();
    }
  }, [notificationCenterItems, refreshNotifications]);

  const clearNotificationCenter = useCallback(async () => {
    // Only clear read notifications via API
    try {
      await notificationsApi.clearRead();
      refreshNotifications();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  }, [refreshNotifications]);

  const unreadCount = unreadCountFromApi;

  const openNotificationCenter = useCallback(() => setIsNotificationCenterOpen(true), []);
  const closeNotificationCenter = useCallback(() => setIsNotificationCenterOpen(false), []);
  const toggleNotificationCenter = useCallback(() => setIsNotificationCenterOpen((prev) => !prev), []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        removeNotification,
        clearAllNotifications,
        notificationCenterItems,
        unreadCount,
        isLoading,
        addToNotificationCenter,
        markAsRead,
        markAllAsRead,
        removeFromNotificationCenter,
        clearNotificationCenter,
        refreshNotifications,
        isNotificationCenterOpen,
        openNotificationCenter,
        closeNotificationCenter,
        toggleNotificationCenter,
      }}
    >
      {children}

      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md">
        {notifications.map((notification) => (
          <Toast
            key={notification.id}
            notification={notification}
            onRemove={() => removeNotification(notification.id)}
          />
        ))}
      </div>

      {/* Notification center panel */}
      {isNotificationCenterOpen && (
        <NotificationCenterPanel
          items={notificationCenterItems}
          isLoading={isLoading}
          onClose={closeNotificationCenter}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onRemove={removeFromNotificationCenter}
          onClear={clearNotificationCenter}
          onRefresh={refreshNotifications}
        />
      )}
    </NotificationContext.Provider>
  );
}

// Convenience hooks for common notification types
export function useToast() {
  const { showNotification } = useNotifications();

  return {
    success: (title: string, message?: string) =>
      showNotification({ type: 'success', title, message }),
    error: (title: string, message?: string) =>
      showNotification({ type: 'error', title, message }),
    warning: (title: string, message?: string) =>
      showNotification({ type: 'warning', title, message }),
    info: (title: string, message?: string) =>
      showNotification({ type: 'info', title, message }),
  };
}

export default NotificationProvider;
